import React, { useMemo } from 'react';
import { Box, Paper, Stack, Typography } from '@mui/material';
import {
  Bar,
  BarChart,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';

const STATUS_COLORS = {
  pending_transfer: '#f59e0b',
  payment_review: '#f97316',
  paid: '#22c55e',
  transfer_received: '#0ea5e9',
  approved: '#16a34a',
  preparing: '#14b8a6',
  shipped: '#6366f1',
  delivered: '#2563eb',
  canceled: '#ef4444'
};

const hashColor = (value) => {
  const text = String(value || 'unknown');
  let hash = 0;
  for (let idx = 0; idx < text.length; idx += 1) {
    hash = text.charCodeAt(idx) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 62%, 46%)`;
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const point = payload[0]?.payload || {};
  return (
    <Paper sx={{ p: 1, borderRadius: 2, border: '1px solid #e5e7eb' }}>
      <Typography variant='caption' sx={{ fontWeight: 700 }}>{label || point.name}</Typography>
      <Typography variant='caption' display='block'>Pedidos: {point.count || 0}</Typography>
      <Typography variant='caption' display='block'>Unidades: {point.total_quantity || point.value || 0}</Typography>
      {point.breakdown && (
        <Typography variant='caption' display='block'>Estados: {point.breakdown}</Typography>
      )}
    </Paper>
  );
};

const DashboardCharts = ({ groups = [], statuses = {} }) => {
  const barData = useMemo(() => {
    return [...groups]
      .sort((a, b) => Number(b.total_quantity || 0) - Number(a.total_quantity || 0))
      .slice(0, 10)
      .map((group) => ({
        name: group.key,
        count: Number(group.count || 0),
        total_quantity: Number(group.total_quantity || 0),
        breakdown: Object.entries(group.statuses || {})
          .map(([status, quantity]) => `${status}: ${quantity}`)
          .join(' | ')
      }));
  }, [groups]);

  const donutData = useMemo(
    () => Object.entries(statuses).map(([name, value]) => ({ name, value: Number(value || 0) })),
    [statuses]
  );

  return (
    <Stack direction={{ xs: 'column', lg: 'row' }} gap={1}>
      <Paper elevation={0} sx={{ flex: 2, minHeight: 320, borderRadius: 3, border: '1px solid #e5e7eb', p: 1.2 }}>
        <Typography sx={{ fontWeight: 700, mb: 1 }}>Total de unidades por grupo</Typography>
        <Box sx={{ width: '100%', height: 270 }}>
          <ResponsiveContainer>
            <BarChart data={barData} margin={{ top: 8, right: 12, left: 8, bottom: 18 }}>
              <XAxis dataKey='name' tick={{ fontSize: 11 }} interval={0} angle={-20} textAnchor='end' height={56} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey='total_quantity' radius={[6, 6, 0, 0]}>
                {barData.map((entry) => (
                  <Cell key={entry.name} fill={hashColor(entry.name)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Paper>

      <Paper elevation={0} sx={{ flex: 1, minHeight: 320, borderRadius: 3, border: '1px solid #e5e7eb', p: 1.2 }}>
        <Typography sx={{ fontWeight: 700, mb: 1 }}>Distribución de estados</Typography>
        <Box sx={{ width: '100%', height: 270 }}>
          <ResponsiveContainer>
            <PieChart>
              <Tooltip content={<CustomTooltip />} />
              <Pie
                data={donutData}
                dataKey='value'
                nameKey='name'
                innerRadius={52}
                outerRadius={94}
                paddingAngle={3}
                labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              >
                {donutData.map((entry) => (
                  <Cell key={entry.name} fill={STATUS_COLORS[entry.name] || hashColor(entry.name)} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        </Box>
      </Paper>
    </Stack>
  );
};

export default DashboardCharts;
